"use client"

import { css, keyframes } from "@emotion/react"
import styled from "@emotion/styled"
import { Box } from "@mui/material"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"
import "swiper/css/effect-fade"
import { Autoplay, EffectFade } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { LeftArrow2Icon, StartIcon, StopSign, transitionTime } from "ui"
import { extractProperty, getUmbracoMediaUrl } from "utils"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

// --- Entrance reveal (once on viewport entry, ASH-1978) ---

function useInViewOnce() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
    rootMargin: "0px 0px -15% 0px",
  })
  const [reduceMotion, setReduceMotion] = useState(false)
  useEffect(() => {
    setReduceMotion(!!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches)
  }, [])
  /* reduced motion: render the end state immediately instead of animating in */
  return { ref, inView: inView || reduceMotion }
}

// --- Caption stagger (ASH-1978) ---

/* The caption lives inside the slide, so Swiper's fade carries it at exactly
   the same rate as the image. These delays hold the text back so the order
   reads image, then title, then description, rather than all three at once.
   Image cross-fade is `transitionTime` (800ms), set as the Swiper speed. */
const CAPTION_TITLE_DELAY_MS = 700
const CAPTION_DESC_DELAY_MS = 1100
const CAPTION_FADE_MS = 800

const captionFadeIn = keyframes`
  from { opacity: 0 }
  to   { opacity: 1 }
`

/* Re-runs whenever $active flips, so it covers both the entrance reveal and
   every later slide change. */
const captionFade = ({ $active, $delay }: { $active: boolean; $delay: number }) => css`
  opacity: 0;
  ${$active &&
  css`
    animation: ${captionFadeIn} ${CAPTION_FADE_MS}ms ease ${$delay}ms forwards;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`

// --- Styled Components (mobile-first, breakpoints.up only) ---

const Section = styled.section`
  display: flex;
  flex-direction: column;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  position: relative;
  padding: 40px 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 60px 0;
  }
`

/* subtle fade-in of the slider once the gallery scrolls into view (ASH-1978);
   no movement under reduced motion */
const FadeIn = styled.div<{ $in: boolean }>`
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transition: opacity 0.7s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const HeaderArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 0 20px;
  margin-bottom: 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0 120px;
    margin-bottom: 40px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 0 240px;
  }
`

const Headline = styled.h2`
  color: var(--comp-heading);
  margin: 0;
  font-size: 40px;
  font-weight: 700;
  line-height: 40px;
  letter-spacing: -0.8px;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
    line-height: 1.2;
  }
`

const Subtitle = styled.p`
  color: var(--comp-text);
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
    line-height: 1.3;
    letter-spacing: -0.48px;
  }
`

const SliderArea = styled.div`
  position: relative;
  width: 100%;
  padding: 0 20px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0 120px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 0 240px;
  }
`

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 347px;
  border-radius: 8px;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: 553px;
  }

  .swiper {
    width: 100%;
    height: 100%;
    padding-block: 0;
  }

  div.swiper-slide {
    position: relative;
    width: 100%;
    height: 100%;
    opacity: 0 !important;
  }

  div.swiper-slide.swiper-slide-active,
  div.swiper-slide.swiper-slide-duplicate-active {
    opacity: 1 !important;
  }
`

/* Darkening is concentrated behind the bottom-right text and fades gradually up
   and to the left, per Figma (ASH-1974) — not a full-width bottom band. The
   gradient is applied via inline style (see OVERLAY_GRADIENT) so the emotion RTL
   plugin can't flip its physical direction. */
const GradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 3;
`

/* darkest corner sits under the text (reading-start bottom): bottom-right in RTL,
   bottom-left in LTR. Inline style (not the styled rule) so stylis-rtl doesn't
   flip it — we pick the direction ourselves from the locale. */
const overlayGradient = (isRtl: boolean) =>
  `linear-gradient(to top ${
    isRtl ? "left" : "right"
  }, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.25) 38%, transparent 62%)`

const SlideTextOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  padding-inline-end: 90px;
  z-index: 4;
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 40px;
    padding-inline-end: 130px;
    gap: 8px;
  }
`

const OverlayTitle = styled.h3<{ $active: boolean; $delay: number }>`
  /* follows the Inner Color Package so editors can control the over-image text
     colour (ASH-1979); falls back to white */
  color: var(--inner-heading, #fff);
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px;
    line-height: 40px;
    letter-spacing: -0.8px;
  }

  ${captionFade}
`

const OverlayDescription = styled.p<{ $active: boolean; $delay: number }>`
  color: var(--inner-text, #fff);
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.36px;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
    line-height: 1.5;
    letter-spacing: -0.4px;
  }

  ${captionFade}
`

const EdgeArrow = styled.button<{ side: "left" | "right" }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background-color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 58px;
  padding: 0;

  ${({ side }) =>
    side === "left"
      ? "left: 0; border-radius: 0 50px 50px 0;"
      : "right: 0; border-radius: 50px 0 0 50px;"}

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: -2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 56px;
    height: 67px;

    svg {
      width: 28px;
      height: 28px;
    }
  }
`

const AutoplayBtn = styled.button`
  position: absolute;
  bottom: 20px;
  inset-inline-end: 20px;
  z-index: 10;
  background: rgba(255, 255, 255, 1);
  opacity: 0.4;
  border: none;
  border-radius: 40px;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    inset-inline-end: 40px;
  }
`

const VideoPlayPauseBtn = styled.button`
  position: absolute;
  top: 20px;
  inset-inline-end: 20px;
  z-index: 15;
  background: rgba(255, 255, 255, 1);
  opacity: 0.4;
  border: none;
  border-radius: 40px;
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    inset-inline-end: 40px;
  }
`

// --- Sub-components ---

function SingleMedia({
  item,
  isRtl,
  isActive,
}: {
  item: { url: string; altText?: string }
  isRtl: boolean
  isActive: boolean
}) {
  const analytics = useLandingAnalytics()
  const url = item?.url || ""
  const isVideo = url.endsWith(".mp4") || url.endsWith(".webm")
  const videoRef = useRef<HTMLVideoElement>(null)
  const [paused, setPaused] = useState(false)

  // Restart video when slide becomes active
  useEffect(() => {
    if (isVideo && videoRef.current && isActive) {
      videoRef.current.currentTime = 0
      videoRef.current.play()
    }
  }, [isActive, isVideo])

  if (isVideo) {
    return (
      <Box sx={{ position: "relative", width: "100%", height: "100%", zIndex: 1 }}>
        <video
          ref={videoRef}
          src={getUmbracoMediaUrl(url)}
          autoPlay
          muted
          loop
          playsInline
          onPlay={() => setPaused(false)}
          onPause={() => setPaused(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        <VideoPlayPauseBtn
          onClick={() => {
            if (videoRef.current) {
              analytics.mediaClick(paused ? "play" : "pause", LANDING_ELEMENTS.gallery)
              if (paused) videoRef.current.play()
              else videoRef.current.pause()
            }
          }}
          aria-label={
            paused ? (isRtl ? "הפעל וידאו" : "Play video") : isRtl ? "עצור וידאו" : "Pause video"
          }
        >
          {paused ? <StartIcon /> : <StopSign />}
        </VideoPlayPauseBtn>
      </Box>
    )
  }

  return (
    <Image
      src={getUmbracoMediaUrl(url)}
      fill
      alt={item?.altText || ""}
      style={{ objectFit: "cover" }}
    />
  )
}

const PaginationDots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  color: var(--comp-text);
`

/* Figma puts a chevron on each side of the dots (ASH-2186): plain glyphs, no
   chip or border, following the section text colour like the dots do. */
const DotsArrow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--comp-text);
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

const Dot = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 100px;
  padding: 0;
  cursor: pointer;
  border: ${({ active }) => (active ? "none" : "1px solid var(--comp-text)")};
  background-color: ${({ active }) => (active ? "var(--comp-text)" : "transparent")};

  &:focus-visible {
    outline: 2px dashed var(--comp-text);
    outline-offset: 2px;
  }
`

// --- Main Component ---

function Gallery6({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale

  const headline = extractProperty(data, "galleryHeadline") as string | undefined
  const subtitle = extractProperty(data, "gallerySubtitle") as string | undefined
  const galleryItems = extractProperty(data, "galleryItems") as any[] | undefined

  const [swiperRef, setSwiperRef] = useState<SwiperType | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const { ref: sectionRef, inView } = useInViewOnce()

  const items = Array.isArray(galleryItems) ? galleryItems : []

  // Parse each gallery item
  const slides = items.map((item: any) => {
    const mediaItems = extractProperty(item, "galleryItemMedia", true) as any[] | undefined
    const media = mediaItems?.[0] || null
    const title = extractProperty(item, "galleryItemTitle") as string | undefined
    const description = extractProperty(item, "galleryItemDescription") as string | undefined
    return { media, title, description }
  })

  const hasSlides = slides.length > 0
  const hasMultipleSlides = slides.length > 1
  const showArrows = hasMultipleSlides
  const showPlayPause = hasMultipleSlides

  const togglePlayPause = useCallback(() => {
    if (swiperRef) {
      if (isPlaying) {
        swiperRef.autoplay.stop()
      } else {
        swiperRef.autoplay.start()
      }
    }
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.gallery)
    setIsPlaying(!isPlaying)
  }, [swiperRef, isPlaying, analytics])

  // Fix pointer-events and z-index for fade+loop: JS overrides after each transition
  useEffect(() => {
    if (!containerRef.current || !hasMultipleSlides) return
    const slideEls = containerRef.current.querySelectorAll(".swiper-slide")
    slideEls.forEach((slide) => {
      const el = slide as HTMLElement
      const isActive =
        el.classList.contains("swiper-slide-active") ||
        el.classList.contains("swiper-slide-duplicate-active")
      if (isActive) {
        el.removeAttribute("inert")
        el.removeAttribute("aria-hidden")
        el.style.pointerEvents = "auto"
        el.style.zIndex = "2"
      } else {
        el.setAttribute("inert", "")
        el.setAttribute("aria-hidden", "true")
        el.style.pointerEvents = "none"
        el.style.zIndex = "0"
      }
    })
  }, [activeIndex, hasMultipleSlides])

  if (!headline && !subtitle && !hasSlides) return null

  return (
    <Section ref={sectionRef}>
      {/* Header: headline + subtitle */}
      {(headline || subtitle) && (
        <HeaderArea>
          {headline && <Headline>{headline}</Headline>}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </HeaderArea>
      )}

      {/* Slider */}
      {hasSlides && (
        <SliderArea>
          <FadeIn $in={inView}>
            <SliderContainer ref={containerRef}>
              {slides.length === 1 ? (
                <>
                  {slides[0].media && <SingleMedia item={slides[0].media} isRtl={isRtl} isActive />}
                  <GradientOverlay style={{ background: overlayGradient(isRtl) }} />
                  {(slides[0].title || slides[0].description) && (
                    <SlideTextOverlay>
                      {slides[0].title && (
                        <OverlayTitle $active={inView} $delay={CAPTION_TITLE_DELAY_MS}>
                          {slides[0].title}
                        </OverlayTitle>
                      )}
                      {slides[0].description && (
                        <OverlayDescription $active={inView} $delay={CAPTION_DESC_DELAY_MS}>
                          {slides[0].description}
                        </OverlayDescription>
                      )}
                    </SlideTextOverlay>
                  )}
                </>
              ) : (
                <>
                  <Swiper
                    loop
                    onSwiper={setSwiperRef}
                    onSlideChange={(swiper) => {
                      setActiveIndex(swiper.realIndex)
                      // Restart video on slide change
                      const activeSlide = swiper.slides[swiper.activeIndex]
                      const video = activeSlide?.querySelector("video")
                      if (video) {
                        video.currentTime = 0
                        video.play()
                      }
                    }}
                    modules={[EffectFade, Autoplay]}
                    effect="fade"
                    speed={transitionTime * 1000}
                    style={{ width: "100%", height: "100%" }}
                    autoplay={
                      isPlaying
                        ? {
                            delay: 4500,
                            disableOnInteraction: false,
                          }
                        : false
                    }
                  >
                    {slides.map((slide, i) => (
                      <SwiperSlide key={i}>
                        {slide.media && (
                          <SingleMedia
                            item={slide.media}
                            isRtl={isRtl}
                            isActive={i === activeIndex}
                          />
                        )}
                        <GradientOverlay style={{ background: overlayGradient(isRtl) }} />
                        {(slide.title || slide.description) && (
                          <SlideTextOverlay>
                            {slide.title && (
                              <OverlayTitle
                                $active={inView && i === activeIndex}
                                $delay={CAPTION_TITLE_DELAY_MS}
                              >
                                {slide.title}
                              </OverlayTitle>
                            )}
                            {slide.description && (
                              <OverlayDescription
                                $active={inView && i === activeIndex}
                                $delay={CAPTION_DESC_DELAY_MS}
                              >
                                {slide.description}
                              </OverlayDescription>
                            )}
                          </SlideTextOverlay>
                        )}
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  {/* Edge arrows */}
                  {showArrows && (
                    <>
                      <EdgeArrow
                        side="left"
                        onClick={() => swiperRef?.slidePrev()}
                        aria-label={isRtl ? "לתמונה הקודמת" : "Previous slide"}
                      >
                        <LeftArrow2Icon
                          color="#000"
                          style={isRtl ? {} : { transform: "rotate(180deg)" }}
                        />
                      </EdgeArrow>
                      <EdgeArrow
                        side="right"
                        onClick={() => swiperRef?.slideNext()}
                        aria-label={isRtl ? "לתמונה הבאה" : "Next slide"}
                      >
                        <LeftArrow2Icon
                          color="#000"
                          style={isRtl ? { transform: "rotate(180deg)" } : {}}
                        />
                      </EdgeArrow>
                    </>
                  )}

                  {/* Autoplay play/pause */}
                  {showPlayPause && (
                    <AutoplayBtn
                      onClick={togglePlayPause}
                      aria-label={
                        isPlaying
                          ? isRtl
                            ? "עצור תנועת הרכיב"
                            : "Stop animation"
                          : isRtl
                          ? "הפעל תנועה רכיב"
                          : "Play animation"
                      }
                    >
                      {isPlaying ? <StopSign fill="#000" /> : <StartIcon fill="#000" />}
                    </AutoplayBtn>
                  )}
                </>
              )}
            </SliderContainer>
            {hasMultipleSlides && (
              <PaginationDots>
                <DotsArrow
                  onClick={() => swiperRef?.slidePrev()}
                  aria-label={isRtl ? "לתמונה הקודמת" : "Previous slide"}
                >
                  <LeftArrow2Icon
                    color="currentColor"
                    style={isRtl ? {} : { transform: "rotate(180deg)" }}
                  />
                </DotsArrow>
                {slides.map((_, i) => (
                  <Dot
                    key={i}
                    active={i === activeIndex}
                    onClick={() => swiperRef?.slideToLoop(i)}
                    aria-label={isRtl ? `עבור לתמונה ${i + 1}` : `Go to slide ${i + 1}`}
                    aria-current={i === activeIndex ? "true" : undefined}
                  />
                ))}
                <DotsArrow
                  onClick={() => swiperRef?.slideNext()}
                  aria-label={isRtl ? "לתמונה הבאה" : "Next slide"}
                >
                  <LeftArrow2Icon
                    color="currentColor"
                    style={isRtl ? { transform: "rotate(180deg)" } : {}}
                  />
                </DotsArrow>
              </PaginationDots>
            )}
          </FadeIn>
        </SliderArea>
      )}
    </Section>
  )
}

export default Gallery6
